import {
  AnalyticsConfig,
  AnalyticsReporter,
  createAnalytics,
} from '@stripe-internal/analytics-js';
import {useContext} from 'react';
import {isProduction} from './env';

const analyticEventNames = [
  'topic_selected',
  'presented_article_clicked',
  'close_button_clicked',
  'toggle_minimize_button_clicked',
  'confirmation_cancel_clicked',
  'confirmation_confirm_clicked',
  'file_attachment_sent',
  'channel_selected',
  'view_payout_clicked',
  'update_bank_account_clicked',
  'failed_payout_article_clicked',
  'payout_status_shown',
  'support_conversations_experiment_frontend_exposure',
  'support_conversations_iframe_loaded',
  'support_conversations_widget_session_retry',
  'support_conversations_widget_shown',
  'ai_assistant_classify_intent_complete',
  'ai_assistant_get_command_complete',
  'ai_assistant_search_clicked',
  'ai_assistant_message_sent',
  'ai_assistant_agent_escalation_created',
] as const;

export type AnalyticEvents = (string | typeof analyticEventNames)[number];

export const config: AnalyticsConfig = {
  clientId: 'support-conversations-srv',
  inProduction: isProduction(),
  sessionization: true,
  // True only for parameters that are "safe" (cannot contain PII). Used for redaction.
  safeParams: {
    topic: true,
    articleUrl: true,
    buttonUrl: true,
    buttonTitle: true,
    universalHelpConversationId: true,
    channel: true,
    payoutStatus: true,
    failureReason: true,
    entrypoint: true,
    experiment_retrieved: true,
    arb_id: true,
    audience: true,
    userId: true,
    milestone: true,
    variant: true,
    resultingSoundSetting: true,
    soundPlayed: true,
    locale: true,
    source: true,
    merchantId: true,
    analyticsCid: true,
    analyticsEventId: true,
    attachmentCount: true,
    mimeType: true,
    errorCode: true,
    enableUniversalHelp: true,
    ctaCopy: true,
    ctaLink: true,
    numTasks: true,
    live_answers_agent_escalation: true,
    live_answers_ai_tooltip_display_ms: true,
    live_answers_answerable: true,
    live_answers_complete: true,
    live_answers_done_type: true,
    live_answers_done_outcome: true,
    live_answers_fetch_call_count: true,
    live_answers_source_collection: true,
    live_answers_source_context: true,
    live_answers_source_score: true,
    live_answers_source_title: true,
    live_answers_source_url: true,
    live_answers_client_fail_reason: true,
    live_answers_client_fail_total_time: true,
    live_answers_no_initial_response_retried: true,
    live_answers_final_timeout_retried: true,
    live_answers_ui_rendering_total_time: true,
    contactInfoShown: true,
    hasPurchaseHistory: true,
    linkCopy: true,
    viewPurchasesCTA: true,
    uh_merchant_payout_status: true,
    uh_merchant_payout_status_destination_type: true,
    uh_merchant_payout_status_failure_code: true,
    uh_merchant_payout_status_failed_update_payout_permission: true,
    uh_merchant_payout_status_first_payout: true,
    uh_merchant_payout_status_has_trace_id: true,
    uh_merchant_payout_status_has_update_payout_permissions: true,
    uh_merchant_payout_status_next_steps_id: true,
    uh_merchant_payout_status_show_manual_payout_message: true,
    uh_verifications_issue_summary_link_href: true,
    uh_verifications_issue_summary_link_copy: true,
    uh_verifications_issue_summary_link_context: true,
    clientType: true,
    conversationId: true,
    messageId: true,
    messageType: true,
    code: true,
    actionName: true,
    method: true,
    has_additional_feedback: true,
    rating: true,
    did_we_resolve: true,
    lets_change_that_option: true,
    ai_assistant_question: true,
    ai_assistant_intent_classification: true,
    ai_assistant_commanding_function_name: true,
    ai_assistant_commanding_result_status: true,
    ai_assistant_commanding_result_text: true,
    ai_assistant_commanding_existing_conversation_id: true,
    ai_assistant_search_query: true,
    ai_assistant_message_content: true,
    ai_assistant_source: true,
    sessionType: true,
    triggerPoint: true,
    feedback: true,
    streaming_conversation_id: true,
    hasFollowUpCase: true,
    followUpCaseOption: true,
    formType: true,
  },
};

export const {
  analyticsContext,
  useAnalytics,
  AnalyticsProvider,
  AnalyticsConsumer,
} = createAnalytics<AnalyticEvents>(config);

export function useAnalyticsReporter() {
  const {analyticsReporter} = useContext(analyticsContext);
  return analyticsReporter as AnalyticsReporter<AnalyticEvents>;
}
